export class GameLogic {
    constructor() {
        this.stepIndex = 0;

        // UI (общие элементы DOM)
        this.controls = document.getElementById('controls');
        this.goodButton = document.getElementById('goodButton');
        this.toxicButton = document.getElementById('toxicButton');
        this.videoElement = document.getElementById('video');
        this.subtitleElement = document.getElementById('subtitle-container');
        this.cctvBtn = document.getElementById('cctvButton');

        // Ссылки на “мониторы” (3D материалы), могут быть 1 или 2
        this.monitorMaterials = [];

        this.sequence = [];
        this.isProcessing = false;

        this.handleChoice = this.handleChoice.bind(this);

        // Backward-compatible: читаем по умолчанию из window.gameState,
        // но позволяем прокинуть материалы через init()
        this._readMonitorMaterialsFromWindow = () => {
            const gs = window?.gameState;
            if (!gs) return [];

            if (Array.isArray(gs.monitorMaterials)) return gs.monitorMaterials;
            if (gs.monitorMaterial) return [gs.monitorMaterial];
            return [];
        };
    }

    async init(config = {}) {
        const {
            dialogsUrl = './assets/dialogs.json',
            scenarioId = null,
            monitorMaterials = null,
            videoElement = null,
            subtitleElement = null,
            controls = null,
            cctvBtn = null,
        } = config;

        // override UI refs (для будущего рефакторинга)
        if (videoElement) this.videoElement = videoElement;
        if (subtitleElement) this.subtitleElement = subtitleElement;
        if (controls) this.controls = controls;
        if (cctvBtn) this.cctvBtn = cctvBtn;

        // override monitor materials
        if (Array.isArray(monitorMaterials)) {
            this.monitorMaterials = monitorMaterials;
        } else {
            this.monitorMaterials = this._readMonitorMaterialsFromWindow();
        }

        try {
            const response = await fetch(dialogsUrl);
            const data = await response.json();

            // 1) старый формат: { sequence: [...] }
            if (Array.isArray(data?.sequence)) {
                this.sequence = data.sequence;
            } else {
                // 2) новый формат: { scenarios: { [id]: { sequence: [...] } } }
                if (scenarioId != null && data?.scenarios?.[scenarioId]?.sequence) {
                    this.sequence = data.scenarios[scenarioId].sequence;
                } else if (scenarioId != null && data?.[scenarioId]?.sequence) {
                    this.sequence = data[scenarioId].sequence;
                } else {
                    console.warn('GameLogic: не найден sequence в dialogs json', { scenarioId, keys: Object.keys(data || {}) });
                    this.sequence = [];
                }
            }
        } catch (e) {
            console.error("Ошибка загрузки диалогов:", e);
        }

        this.setupEventListeners();
    }

    setupEventListeners() {
        // Обработчики для кнопок диалога
        const handler = (type) => (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleChoice(type);
        };

        if (this.goodButton) this.goodButton.onpointerdown = handler('good');
        if (this.toxicButton) this.toxicButton.onpointerdown = handler('toxic');
        
        // Обработчик кнопки камер
        if (this.cctvBtn) {
            this.cctvBtn.onpointerdown = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.cctvBtn.style.display = 'none';
                await this.playVideo('./assets/videos/5.Meet.mp4');
                await this.playVideo('./assets/videos/5.Camera.mp4');
                this.nextStep(); 
            };
        }

        // --- ФИНАЛЬНЫЙ ВЫБОР ---
        // Ищем все кнопки внутри финального оверлея
        const culpritButtons = document.querySelectorAll('.culprit-btn');
        culpritButtons.forEach(btn => {
            btn.onpointerdown = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const name = e.target.getAttribute('data-name');
                this.selectCulprit(name);
            };
        });
    }

    async selectCulprit(name) {
        const finalOverlay = document.getElementById('final-choice-overlay');
        if (finalOverlay) finalOverlay.style.display = 'none';

        if (name === 'Начальник') {
            // Если выбран начальник — победное/финальное видео
            console.log("Финал: Выбран начальник");
            await this.playVideo('./assets/videos/11.End.mp4');
            alert("Вы признали вину. Игра окончена.");
        } else {
            // Если выбран кто-то другой — проигрыш
            if (confirm(`Вы обвинили ${name}, но это не он. Начать созвоны заново?`)) {
                location.reload();
            }
        }
    }

    async startSequence() {
        if (window.gameState.startBtn) window.gameState.startBtn.visible = false;
        this.stepIndex = 0;
        await this.processCurrentStep();
    }

    async processCurrentStep() {
        const step = this.sequence[this.stepIndex];
        if (!step) {
            this.showFinalChoice();
            return;
        }

        if (step.introSubtitle) await this.showSubtitle(step.introSubtitle);
        await this.playVideo(step.initialVideo);
        
        if (this.goodButton) this.goodButton.innerText = step.choices.good.label;
        if (this.toxicButton) this.toxicButton.innerText = step.choices.toxic.label;
        
        this.showControls();
    }

    async handleChoice(choiceType) {
        if (this.isProcessing) return;
        this.isProcessing = true;

        const step = this.sequence[this.stepIndex];
        const choice = step.choices[choiceType];
        
        this.hideControls();

        if (choice.ggSubtitle) await this.showSubtitle(choice.ggSubtitle);
        await this.playVideo(choice.video);

        this.isProcessing = false;

        if (step.showCCTV && this.cctvBtn) {
            this.cctvBtn.style.display = 'block';
        } else {
            this.nextStep();
        }
    }

    nextStep() {
        this.stepIndex++;
        this.processCurrentStep();
    }

    async playVideo(path) {
        return new Promise((resolve) => {
            if (!path) return resolve();

            this.videoElement.src = path;
            // this.videoElement.playbackRate = 16.0; 
            
            let settled = false;
            const finish = () => {
                if (!settled) {
                    settled = true;
                    resolve();
                }
            };

            this.videoElement.onended = finish;
            this.videoElement.onerror = () => {
                console.warn(`Видео не найдено: ${path}. Пропускаю...`);
                setTimeout(finish, 500);
            };
            
            const gs = window?.gameState;
            const textureRef = gs?.texture;

            const materials = this.monitorMaterials?.length
                ? this.monitorMaterials
                : (gs?.monitorMaterials?.length ? gs.monitorMaterials : (gs?.monitorMaterial ? [gs.monitorMaterial] : []));

            if (materials && textureRef) {
                materials.forEach((m) => {
                    if (!m) return;
                    m.map = textureRef;
                    m.needsUpdate = true;
                });
            }
            
            this.videoElement.play().catch(() => setTimeout(finish, 500));
        });
    }

    async showSubtitle(text, duration = 2000) {
        if (!this.subtitleElement) return;
        this.subtitleElement.innerText = text;
        this.subtitleElement.style.display = 'block';
        await new Promise(res => setTimeout(res, duration));
        this.subtitleElement.style.display = 'none';
    }

    showFinalChoice() {
        const finalOverlay = document.getElementById('final-choice-overlay');
        if (finalOverlay) finalOverlay.style.display = 'flex';
    }

    hideControls() { if (this.controls) this.controls.style.display = 'none'; }
    showControls() { if (this.controls) this.controls.style.display = 'flex'; }
}